import { Injectable } from '@angular/core';
import { IdleDeadline } from 'requestidlecallback';
import TinyQueue from 'tinyqueue';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { IdleCallbackService } from './idle-callback.service';
import { PageVisibilityService } from './page-visibility.service';
import { Performance, PerformanceService } from './performance.service';
import { WindowService } from './window.service';

export const enum SchedulePriority {
    Immediate = 0,
    High = 1,
    Medium = 2,
    Low = 3,
    VeryLow = 4
}

interface ScheduledWork {
    cancelled: boolean;
    completed: boolean;
    readonly work: (deadline?: IdleDeadline) => Promise<any> | void;
    readonly priority: SchedulePriority;
}

export interface ScheduledWorkHandle {
    cancel: () => void;
    cancelAndRunNow: () => void;
    adjustPriority(targetPriority: SchedulePriority): ScheduledWorkHandle;
}

@Injectable({
    providedIn: 'root'
})
export class SchedulerService {
    private readonly heap = new TinyQueue<ScheduledWork>(
        [],
        (a, b) => this.compare(a, b)
    );

    private firstIdle: number;
    private idleCallbackHandle: number;
    private isProcessing = false;
    private shouldLogFirstIdle = this.pageVisibilityService.isVisible(); // We cannot accurately log "First Idle" if tab is hidden at any point.
    private shouldLogFirstSchedule = true;

    constructor(
        private readonly idleCallbackService: IdleCallbackService,
        private readonly errorHandler: OfficeHomePageErrorHandler,
        private readonly pageVisibilityService: PageVisibilityService,
        private readonly windowService: WindowService,
        private readonly performanceService: PerformanceService
    ) {
        this.setOnVisibilityChange();
        this.setOnGlobalWindow();
    }

    public scheduleWork(work: () => Promise<any> | any, priority: SchedulePriority): ScheduledWorkHandle {
        if (priority === SchedulePriority.Immediate) {
            work();

            // Cannot cancel work that's of SchedulePriority === Immediate.
            return this.createNoopHandle();
        }

        if (this.shouldLogFirstSchedule) {
            this.shouldLogFirstSchedule = false;
            this.performanceService.mark('First_Schedule');
        }

        const scheduledWork: ScheduledWork = { work, priority, cancelled: false, completed: false };

        const workHandle = this.createHandle(scheduledWork);

        this.heap.push(scheduledWork);

        this.dequeueWhenIdle();

        return workHandle;
    }

    public hasTasks(): boolean {
        return !!this.heap.peek();
    }

    public get FCI(): number {
        return typeof(this.firstIdle) !== 'undefined' ? this.firstIdle : -1;
    }

    private createHandle(scheduledWork: ScheduledWork): ScheduledWorkHandle {
        // Flag work as cancelled. It will be skipped when the scheduler tries to run this task.
        // If work was already completed, nothing will happen.
        const cancel = () => scheduledWork.cancelled = true;

        // Flags work as cancelled, but also run the work immediately. It also marks the work as completed.
        // If work was already completed, nothing will happen.
        const cancelAndRunNow = () => {
            // Work has already been cancelled, it cannot be run.
            if (scheduledWork.cancelled) {
                return;
            }

            cancel();

            if (!scheduledWork.completed) {
                scheduledWork.work();
                scheduledWork.completed = true;
            }
        };

        // Allows pending work to move up or down in priority. Is a noop for work that's already been run.
        const adjustPriority = (targetPriority: SchedulePriority) => {
            cancel();

            // if the work has already completed, adjust priority shouldn't do anything.
            if (scheduledWork.completed) {
                return this.createNoopHandle();
            }

            // re-schedule the work with the new priority.
            return this.scheduleWork(scheduledWork.work, targetPriority);
        };

        return {
            cancel,
            cancelAndRunNow,
            adjustPriority
        };
    }

    private createNoopHandle() {
        return {
            cancel: () => null,
            cancelAndRunNow: () => null,
            adjustPriority: () => null
        };
    }

    private dequeueWhenIdle() {
        // If the page is hidden, do all pending work ASAP.
        if (!this.pageVisibilityService.isVisible()) {
            this.doWork();
            return;
        }

        // If there is already an IdleCallback handle, we have already scheduled
        // work to happen when idle.
        if (this.idleCallbackHandle) {
            return;
        }

        // Schedule work to run when idle.
        this.idleCallbackHandle = this.idleCallbackService.runWhenIdle(deadline => {
            if (this.shouldLogFirstIdle) {
                this.shouldLogFirstIdle = false;
                this.firstIdle = Performance.now();
                this.performanceService.mark('First_Idle');
            }

            this.idleCallbackHandle = null;
            this.doWork(deadline);
        });
    }

    private async doWork(deadline?: IdleDeadline): Promise<void> {
        if (this.isProcessing) {
            return;
        }

        this.isProcessing = true;

        while (
            !this.pageVisibilityService.isVisible() ||
            deadline && deadline.timeRemaining() > 0
        ) {
            const scheduledWork = this.heap.pop();

            if (!scheduledWork) {
                break;
            }

            if (scheduledWork.cancelled) {
                continue;
            }

            try {
                await scheduledWork.work(deadline);
                scheduledWork.completed = true;
            } catch (ex) {
                this.errorHandler.handleError(ex, ErrorId.DXUlK);
            }
        }

        this.isProcessing = false;

        // If we run out of time, but there are more tasks to do, schedule them for later.
        if (this.heap.peek()) {
            this.dequeueWhenIdle();
        }
    }

    private compare(a: ScheduledWork, b: ScheduledWork) {
        return a.priority < b.priority ? -1 : a.priority > b.priority ? 1 : 0;
    }

    private setOnGlobalWindow() {
        this.windowService.nativeWindow.scheduler = this;
    }

    private setOnVisibilityChange() {
        this.pageVisibilityService.addOnVisibilityChanged(state => {
            if (state !== 'hidden') {
                return;
            }

            // We cannot accurately log "First Idle" if the tab become hidden at any point.
            this.shouldLogFirstIdle = false;

            if (this.idleCallbackHandle) {
                this.idleCallbackService.cancelIdleCallback(this.idleCallbackHandle);
                this.idleCallbackHandle = null;
            }

            this.doWork();
        });
    }
}
